import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Form, Row, Col, Badge, Spinner, Tabs, Tab } from "react-bootstrap";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import axios from "axios";
import toast from "react-hot-toast";
import { BsArrowLeft, BsCheckCircleFill, BsShieldCheck, BsDownload } from "react-icons/bs";
import AuthContext from "../../../components/shared/Context";
import AbhaCreateWizard from "../../../components/Abdm/AbhaCreateWizard";

const AbhaManagementChild = ({ abhaManagementRecord }) => {
  const navigate = useNavigate();
  const { loggedInUserDetails } = useContext(AuthContext);
  const [{ decryptData, encryptData }] = useAtom(gbState);

  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [newAbhaData, setNewAbhaData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [profileDetails, setProfileDetails] = useState(null);
  const [abhaCard, setAbhaCard] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Mobile & Email update states
  const [mobileToUpdate, setMobileToUpdate] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [showMobileOtpField, setShowMobileOtpField] = useState(false);
  const [mobileOtpTxnId, setMobileOtpTxnId] = useState("");
  const [updatingMobile, setUpdatingMobile] = useState(false);

  const [emailToVerify, setEmailToVerify] = useState("");
  const [sendingEmailLink, setSendingEmailLink] = useState(false);

  // Initialize Aadhaar & Mobile from record
  useEffect(() => {
    if (abhaManagementRecord) {
      const rawCard = abhaManagementRecord?.id_card?.card_number;
      const decryptedCard = rawCard && rawCard.length > 12 ? decryptData(rawCard) : rawCard;
      const cleanAadhaar = decryptedCard ? decryptedCard.replace(/\D/g, "") : "";
      setAadhaarNumber(cleanAadhaar || "");
      const cleanMobile = abhaManagementRecord?.contact?.primary_mobile
        ? abhaManagementRecord.contact.primary_mobile.replace(/\D/g, "")
        : "";
      setMobileNumber(cleanMobile || "");
    }
  }, [abhaManagementRecord, decryptData]);

  // Fetch ABHA profile details and card on verification success
  useEffect(() => {
    if (newAbhaData?.tokens?.token) {
      const fetchVerifiedProfile = async () => {
        setLoadingProfile(true);
        const userToken = localStorage.getItem("token");
        try {
          // Fetch account profile details
          const profileRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/abdm/abha/profile/account`,
            { token: newAbhaData.tokens.token },
            {
              headers: { Authorization: `Bearer ${userToken}` },
            }
          );
          setProfileDetails(profileRes.data || null);

          // Fetch card binary
          const cardRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/abdm/abha/profile/account/abha-card`,
            { token: newAbhaData.tokens.token },
            {
              headers: { Authorization: `Bearer ${userToken}` },
            }
          );
          setAbhaCard(cardRes.data || null);
        } catch (error) {
          console.error("Failed to fetch verified profile/card:", error);
          toast.error("Failed to load ABHA profile details or card from NHA registry.");
        } finally {
          setLoadingProfile(false);
        }
      };
      fetchVerifiedProfile();
    } else {
      setProfileDetails(null);
      setAbhaCard(null);
    }
  }, [newAbhaData]);

  const handleDownloadCard = () => {
    if (!abhaCard?.base64) {
      toast.error("ABHA card data not loaded yet.");
      return;
    }
    const mimeType = abhaCard.contentType || "image/png";
    const extension = mimeType.includes("pdf") ? "pdf" : "png";
    const dataUrl = `data:${mimeType};base64,${abhaCard.base64}`;

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `ABHA_Card_${newAbhaData?.abha_number || abhaManagementRecord?.patient_name || "card"}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("ABHA Card downloaded successfully.");
  };

  const handleAbhaCreated = (result) => {
    setNewAbhaData(result);
    setShowWizard(false);
    toast.success("ABHA details verified successfully!");
    setActiveTab("card");
  };

  const handleSendMobileOtp = async () => {
    if (!mobileToUpdate || mobileToUpdate.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    const currentTxnId = newAbhaData?.txnId;
    if (!currentTxnId) {
      toast.error("No active verification transaction found. Please re-verify.");
      return;
    }

    setUpdatingMobile(true);
    const userToken = localStorage.getItem("token");
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/abdm/abha/mobile/update/request-otp`,
        { txnId: currentTxnId, mobile: mobileToUpdate },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      setMobileOtpTxnId(data.txnId || currentTxnId);
      setShowMobileOtpField(true);
      toast.success("OTP sent to the new mobile number successfully!");
    } catch (err) {
      console.error("Failed to request mobile update OTP:", err);
      toast.error(err?.response?.data?.message || "Failed to send OTP to new mobile.");
    } finally {
      setUpdatingMobile(false);
    }
  };

  const handleVerifyMobileOtp = async () => {
    if (!mobileOtp || mobileOtp.length < 4) {
      toast.error("Please enter the OTP received.");
      return;
    }
    const activeTxnId = mobileOtpTxnId || newAbhaData?.txnId;
    if (!activeTxnId) {
      toast.error("Transaction expired. Please try again.");
      return;
    }

    setUpdatingMobile(true);
    const userToken = localStorage.getItem("token");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/abdm/abha/mobile/update/verify-otp`,
        { txnId: activeTxnId, otp: mobileOtp },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      toast.success("Mobile number verified and updated in ABHA successfully!");
      setShowMobileOtpField(false);
      setMobileOtp("");
    } catch (err) {
      console.error("Failed to verify mobile OTP:", err);
      toast.error(err?.response?.data?.message || "Failed to verify new mobile number.");
    } finally {
      setUpdatingMobile(false);
    }
  };

  const handleSendEmailLink = async () => {
    if (!emailToVerify || !emailToVerify.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const userAbAccessToken = newAbhaData?.tokens?.token;
    if (!userAbAccessToken) {
      toast.error("Active session required. Please verify patient via Aadhaar OTP.");
      return;
    }

    setSendingEmailLink(true);
    const userToken = localStorage.getItem("token");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/abdm/abha/profile/account/email/verify-link`,
        { token: userAbAccessToken, email: emailToVerify },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      toast.success("Email verification link sent successfully! Please check your inbox.");
      setEmailToVerify("");
    } catch (err) {
      console.error("Failed to send email link:", err);
      toast.error(err?.response?.data?.message || "Failed to send email verification link.");
    } finally {
      setSendingEmailLink(false);
    }
  };

  const removeDashes = (str) => {
    return str ? str.replace(/-/g, "") : "";
  };

  const handleSaveLink = async () => {
    if (!newAbhaData) {
      toast.error("Please verify ABHA before saving.");
      return;
    }

    setSaving(true);
    const userToken = localStorage.getItem("token");

    // We will encrypt the newly verified Aadhaar number if it changed
    const encryptedCardNumber = encryptData(removeDashes(aadhaarNumber));

    const payload = {
      abha_number: newAbhaData.abha_number,
      abha_address: newAbhaData.abha_address,
      link_status: "linked",
      linked_at: new Date().toISOString(),
      last_gateway_txn_id: newAbhaData.txnId,
      card_number: encryptedCardNumber, // Updates the Aadhaar card number as well
    };

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/patient/${abhaManagementRecord._id}/abdm`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      toast.success("ABHA linked and Aadhaar updated successfully.");
      navigate("/patients");
    } catch (error) {
      console.error("Failed to link ABHA:", error);
      toast.error(error?.response?.data?.message || "Failed to update ABHA details.");
    } finally {
      setSaving(false);
    }
  };

  const isLinked = abhaManagementRecord?.abdm?.link_status === "linked";

  return (
    <div className="abha-mgmt-container py-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
        <Button
          variant="outline-secondary"
          size="sm"
          className="me-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: "36px", height: "36px", padding: 0 }}
          onClick={() => navigate("/patients")}
        >
          <BsArrowLeft size={18} />
        </Button>
        <div>
          <h4 className="m-0 fw-bold text-dark">ABHA ID Management</h4>
          <span className="text-muted fs-7">Manage and re-verify patient health credentials</span>
        </div>
      </div>

      <Card className="shadow-sm border-0 mb-4 rounded-3 info-card-bg">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h4 className="fw-bold text-primary mb-1">
                {abhaManagementRecord?.patient_name}
              </h4>
              <span className="text-muted fs-7">
                Patient ID: {abhaManagementRecord?.patientId}
              </span>
            </div>
            <Badge bg={isLinked ? "success" : "secondary"} className="px-3 py-2 fs-7">
              {isLinked ? "Linked" : "Not Linked"}
            </Badge>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-4">
          <Tabs
            id="abha-management-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4 abha-tabs"
          >
            <Tab eventKey="overview" title="Link Status & Overview">
              <div className="py-2">
                <h5 className="fw-bold mb-3 text-dark">Current Credential Details</h5>
                <Row className="g-4 mb-4">
                  <Col md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="d-block text-muted fs-8 text-uppercase">Current Aadhaar</span>
                      <strong className="text-dark fs-6">
                        {abhaManagementRecord?.id_card?.card_number
                          ? decryptData(abhaManagementRecord.id_card.card_number)
                              .replace(/(.{4})/g, "$1 ")
                              .trim()
                          : "Not Provided"}
                      </strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="d-block text-muted fs-8 text-uppercase">Current ABHA Number</span>
                      <strong className="text-dark fs-6">
                        {abhaManagementRecord?.abdm?.abha_number || "—"}
                      </strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="d-block text-muted fs-8 text-uppercase">Current ABHA Address</span>
                      <strong className="text-dark fs-6">
                        {abhaManagementRecord?.abdm?.abha_address || "—"}
                      </strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="d-block text-muted fs-8 text-uppercase">Linked Date</span>
                      <strong className="text-dark fs-7">
                        {abhaManagementRecord?.abdm?.linked_at
                          ? new Date(abhaManagementRecord.abdm.linked_at).toLocaleString()
                          : "Not Linked"}
                      </strong>
                    </div>
                  </Col>
                </Row>

                <div className="mt-4 pt-3 border-top border-light-subtle text-muted fs-8 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <BsShieldCheck className="text-success" size={16} />
                    <span>ABHA credentials stored securely in base64 format.</span>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => setActiveTab("verify")}>
                    Go to Verification
                  </Button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="card" title="ABHA ID Card & Profile">
              <div className="py-2">
                {loadingProfile ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" role="status" className="mb-2" />
                    <div className="text-muted fs-7">Fetching verified profile and ABHA card from ABDM registry...</div>
                  </div>
                ) : abhaCard?.base64 || profileDetails ? (
                  <Row className="g-4">
                    {abhaCard?.base64 && (
                      <Col lg={6} md={12} className="text-center">
                        <h6 className="text-muted fw-semibold text-start fs-8 text-uppercase mb-3">Official ABHA ID Card</h6>
                        <div className="d-inline-block position-relative bg-white p-3 border rounded shadow-sm mb-3">
                          <img
                            src={`data:${abhaCard.contentType || "image/png"};base64,${abhaCard.base64}`}
                            alt="ABHA Card"
                            className="img-fluid"
                            style={{ maxHeight: "280px", borderRadius: "4px" }}
                          />
                        </div>
                        <div>
                          <Button
                            variant="primary"
                            onClick={handleDownloadCard}
                            className="d-inline-flex align-items-center gap-2 fw-semibold px-4 py-2 shadow-sm"
                          >
                            <BsDownload size={16} />
                            Download ABHA ID Card
                          </Button>
                        </div>
                      </Col>
                    )}

                    <Col lg={abhaCard?.base64 ? 6 : 12} md={12}>
                      <h6 className="text-muted fw-semibold fs-8 text-uppercase mb-3 border-bottom pb-2">Verified Registry Profile</h6>
                      <Row className="g-3 text-dark fs-7">
                        <Col sm={6}>
                          <span className="text-muted d-block fs-8 text-uppercase">Full Name</span>
                          <strong className="fs-6">{profileDetails?.name || [profileDetails?.firstName, profileDetails?.lastName].filter(Boolean).join(" ") || "—"}</strong>
                        </Col>
                        <Col sm={6}>
                          <span className="text-muted d-block fs-8 text-uppercase">Gender</span>
                          <strong className="fs-6">{profileDetails?.gender || "—"}</strong>
                        </Col>
                        <Col sm={6}>
                          <span className="text-muted d-block fs-8 text-uppercase">Date of Birth</span>
                          <strong className="fs-6">
                            {profileDetails?.dob || 
                             (profileDetails?.dayOfBirth && profileDetails?.monthOfBirth && profileDetails?.yearOfBirth 
                               ? `${profileDetails.dayOfBirth}/${profileDetails.monthOfBirth}/${profileDetails.yearOfBirth}`
                               : profileDetails?.yearOfBirth || "—")}
                          </strong>
                        </Col>
                        <Col sm={6}>
                          <span className="text-muted d-block fs-8 text-uppercase">Mobile Number</span>
                          <strong className="fs-6">{profileDetails?.mobile || "—"}</strong>
                        </Col>
                        <Col sm={12}>
                          <span className="text-muted d-block fs-8 text-uppercase">Address</span>
                          <strong className="fs-6">
                            {[
                              profileDetails?.address,
                              profileDetails?.districtName,
                              profileDetails?.stateName,
                              profileDetails?.pincode
                            ].filter(Boolean).join(", ") || "—"}
                          </strong>
                        </Col>
                      </Row>

                      {newAbhaData && (
                        <div className="mt-4 p-3 bg-success-subtle border border-success-subtle rounded-3 transition-fade">
                          <div className="d-flex align-items-center gap-2 mb-2 text-success fw-bold">
                            <BsCheckCircleFill size={18} />
                            <span>Verification Completed in Current Session</span>
                          </div>
                          <p className="fs-8 text-muted mb-3">
                            These verified details have not been saved to the database yet. Click the button below to update the patient's record.
                          </p>
                          <div className="d-flex justify-content-end">
                            <Button
                              variant="success"
                              onClick={handleSaveLink}
                              disabled={saving}
                              className="px-4 py-2 fw-semibold rounded-2 shadow-sm"
                            >
                              {saving ? "Saving..." : "Save & Link Patient"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3 text-muted fs-5">No active verification session</div>
                    <p className="text-muted fs-7 max-w-500 mx-auto mb-4">
                      To retrieve and download the official ABHA ID card or view verified profile details from the NHA registry, the patient must authenticate via OTP.
                    </p>
                    <Button variant="primary" onClick={() => setActiveTab("verify")}>
                      Authenticate via Aadhaar OTP
                    </Button>
                  </div>
                )}
              </div>
            </Tab>

            {newAbhaData && (
              <Tab eventKey="credentials" title="Verify Mobile & Email">
                <div className="py-2">
                  <h5 className="fw-bold mb-3 text-dark">Update ABHA Mobile / Email</h5>
                  <p className="text-muted fs-7 mb-4">
                    Link a verified mobile number or email address to the patient's ABHA account.
                  </p>

                  <Row className="g-4">
                    {/* Mobile Section */}
                    <Col md={6}>
                      <Card className="border shadow-sm h-100 rounded-3">
                        <Card.Body className="p-4 d-flex flex-column justify-content-between">
                          <div>
                            <h6 className="fw-bold mb-3 text-primary">Update Mobile Number</h6>
                            <Form onSubmit={(e) => e.preventDefault()}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold text-muted fs-8 text-uppercase">
                                  New Mobile Number
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter 10-digit mobile number"
                                  maxLength={10}
                                  value={mobileToUpdate}
                                  onChange={(e) => setMobileToUpdate(e.target.value.replace(/\D/g, ""))}
                                  disabled={showMobileOtpField || updatingMobile}
                                  className="form-control-lg rounded-2 fs-6 py-2 border-dark-subtle"
                                />
                              </Form.Group>

                              {showMobileOtpField && (
                                <Form.Group className="mb-3 transition-fade">
                                  <Form.Label className="fw-semibold text-muted fs-8 text-uppercase">
                                    Enter 6-digit OTP
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter OTP"
                                    maxLength={6}
                                    value={mobileOtp}
                                    onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, ""))}
                                    disabled={updatingMobile}
                                    className="form-control-lg rounded-2 fs-6 py-2 border-dark-subtle"
                                  />
                                </Form.Group>
                              )}
                            </Form>
                          </div>

                          <div className="d-grid mt-3">
                            {!showMobileOtpField ? (
                              <Button
                                variant="primary"
                                onClick={handleSendMobileOtp}
                                disabled={updatingMobile || mobileToUpdate.length !== 10}
                                className="py-2.5 fw-semibold rounded-2"
                              >
                                {updatingMobile ? <Spinner animation="border" size="sm" /> : "Send OTP"}
                              </Button>
                            ) : (
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => setShowMobileOtpField(false)}
                                  disabled={updatingMobile}
                                  className="w-100 py-2 fw-semibold rounded-2"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="success"
                                  onClick={handleVerifyMobileOtp}
                                  disabled={updatingMobile || mobileOtp.length < 4}
                                  className="w-100 py-2 fw-semibold rounded-2"
                                >
                                  {updatingMobile ? <Spinner animation="border" size="sm" /> : "Verify OTP"}
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Email Section */}
                    <Col md={6}>
                      <Card className="border shadow-sm h-100 rounded-3">
                        <Card.Body className="p-4 d-flex flex-column justify-content-between">
                          <div>
                            <h6 className="fw-bold mb-3 text-primary">Verify Email Address</h6>
                            <Form onSubmit={(e) => e.preventDefault()}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold text-muted fs-8 text-uppercase">
                                  Email Address
                                </Form.Label>
                                <Form.Control
                                  type="email"
                                  placeholder="patient@example.com"
                                  value={emailToVerify}
                                  onChange={(e) => setEmailToVerify(e.target.value)}
                                  disabled={sendingEmailLink}
                                  className="form-control-lg rounded-2 fs-6 py-2 border-dark-subtle"
                                />
                              </Form.Group>
                            </Form>
                          </div>

                          <div className="d-grid mt-3">
                            <Button
                              variant="primary"
                              onClick={handleSendEmailLink}
                              disabled={sendingEmailLink || !emailToVerify || !emailToVerify.includes("@")}
                              className="py-2.5 fw-semibold rounded-2"
                            >
                              {sendingEmailLink ? <Spinner animation="border" size="sm" /> : "Send Email Verification Link"}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Tab>
            )}

            <Tab eventKey="verify" title="Re-verify / Change ABHA">
              <div className="py-2">
                <h5 className="fw-bold mb-3 text-dark">ABHA Re-Verification & Linkage</h5>
                <p className="text-muted fs-7 mb-4">
                  Verify the patient's identity with Aadhaar via OTP authentication. Once verified,
                  you can update their ABHA profile and link it to their iCare record.
                </p>

                <Form onSubmit={(e) => e.preventDefault()} style={{ maxWidth: "600px" }}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-muted fs-8 text-uppercase">
                      Aadhaar Card Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 12-digit Aadhaar Number"
                      maxLength={12}
                      value={aadhaarNumber}
                      onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ""))}
                      className="form-control-lg rounded-2 fs-6 py-2 border-dark-subtle"
                    />
                    <Form.Text className="text-muted fs-8">
                      Note: Changing this Aadhaar number will initiate verification for the new identity.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-muted fs-8 text-uppercase">
                      Mobile Number for Communication
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ""))}
                      className="form-control-lg rounded-2 fs-6 py-2 border-dark-subtle"
                    />
                  </Form.Group>

                  <div className="d-grid mb-3">
                    <Button
                      variant="primary"
                      size="lg"
                      className="py-2.5 fw-semibold rounded-2 shadow-sm"
                      disabled={!aadhaarNumber || aadhaarNumber.length !== 12}
                      onClick={() => setShowWizard(true)}
                    >
                      Start ABHA Verification Wizard
                    </Button>
                  </div>
                </Form>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Verification Wizard Modal */}
      {showWizard && (
        <AbhaCreateWizard
          show={showWizard}
          onHide={() => setShowWizard(false)}
          initialAadhaar={aadhaarNumber}
          initialMobile={mobileNumber}
          onComplete={handleAbhaCreated}
        />
      )}
    </div>
  );
};

export default AbhaManagementChild;